import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router';
import { Body5, Body7 } from '@ui';
import { ROUTES } from '@constants/routes';
import { REVIEW_SUBJECTS } from '@services/fmdc';
import { Chip } from '@features/scoreSpec/styles';
import ChevronDown from '@assets/icons/svgs/ChevronDown';
import { Row, rowKeyHandler } from './styles';
import { ConfidenceDot } from './ConfidenceDot';
import { StatusPill } from './StatusPill';
import { SelectionCheckbox } from './SelectionCheckbox';
import { fmdcPaths } from './fmdcUrl';
import {
  EM_DASH,
  formatConfidence,
  formatCount,
  formatReviewReason,
  formatReviewerStatus,
  formatSampleValues,
  valueKindForGroup,
  formatValidationStatus,
  reviewReasonTone,
} from './format';
import { REVIEWER_STATUS_TOKENS, VALIDATION_TONES, tokenFor } from './statusTokens';

// One review group — a spreadsheet row — as a grid row. An extraction group expands in place to
// its cells; an unmapped group opens its own detail, because it is decided through a different
// endpoint and `POST /review_groups/decide` answers 400 for the whole batch if one of its ids is
// unmapped. That is also why an unmapped row has NO checkbox: the exclusion is the single source of
// truth for what a bulk action may carry.

// ☐ | Sheet · row | Row label | Item | Split · metric | Reasons | Confidence | Valid. | Cells | Sample values | Status | ▾
//
// The locator (Sheet · row) has the widest floor of the text columns, and the free-text columns to
// its right the narrowest: when the window squeezes the table, it is the sheet name and row number
// a reviewer needs to find the row in the model, and those were the first to wrap and lose the
// number at 90px while Sample values held 170px of text that already truncates to a tooltip. A
// fixed floor rather than a content-sized track (`fit-content`) because every Row is its own grid —
// sized per row, the column would be a different width on every line.
export const REVIEW_GRID_COLUMNS =
  '28px minmax(160px, 0.7fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) minmax(100px, 0.9fr) minmax(120px, 1.2fr) 72px minmax(64px, 0.6fr) 44px minmax(120px, 1.4fr) minmax(90px, 0.8fr) 28px';

// The floors above sum to 1096px, plus eleven 16px gaps and the row's 16px of padding. Below this
// the table scrolls sideways; at or above it no column is under its floor.
export const REVIEW_TABLE_MIN_WIDTH = 1096 + 11 * 16 + 16;

const Truncate = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

// Sheet name then row number on one line, and only the name gives way: it truncates (the full
// text is in the title), the number is pinned. Excel caps a sheet name at 31 characters, so the
// 160px floor shows most of them whole.
const Locator = styled.div`
  display: flex;
  align-items: baseline;
  min-width: 0;
`;

// A span around the Body7, for the reason MixedTag below is one.
const RowNumber = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: 4px;
`;

// A span around the Body7, not `styled(Body7)`: that would drop BaseText's `base-text` class.
const MixedTag = styled.span`
  cursor: help;
`;

// The trailing cue: what a click on this row does. An extraction row expands in place, so it
// gets AuditEventRow's rotating chevron; an unmapped row navigates away, so it gets a ›. Both are
// decorative — the row itself carries aria-expanded and the whole-row label.
const Affordance = styled.div`
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

const Chevron = styled.span`
  display: inline-flex;
  width: 14px;
  height: 14px;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  transition: transform 120ms ease;

  svg {
    width: 14px;
    height: 14px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MIXED_NOTE = 'Cells map to different items; a decision here is not saved for the issuer.';

const isUnmapped = (group) => group.subject === REVIEW_SUBJECTS.UNMAPPED;

const reviewerStatusOf = (group) => (isUnmapped(group) ? group.reviewer_decision || 'pending' : group.reviewer_status);

const sampleText = (group) =>
  isUnmapped(group)
    ? group.values_preview || EM_DASH
    : // growth_pct and the other *_pct components are fractions on the wire; the component name is
      // the only hint a group carries about how to show them.
      formatSampleValues(group.sample_values, { valueKind: valueKindForGroup(group) });

const itemText = (group) => {
  if (isUnmapped(group)) {
    return group.candidate_item_code || group.proposed_new_catalog_code || EM_DASH;
  }
  return group.item_label || group.canonical_item_code || EM_DASH;
};

const splitText = (group) => {
  const parts = [];
  if (group.split_label) parts.push(group.split_type ? `${group.split_type}: ${group.split_label}` : group.split_label);
  else if (group.split_type) parts.push(group.split_type);
  if (group.metric_component) parts.push(group.metric_component);
  return parts.length ? parts.join(' · ') : EM_DASH;
};

// aria-label on role="button" REPLACES the row's inner text, so this string is the whole row for a
// screen reader. The leading "<label> row" is what lookups match on; the rest is appended.
export const rowAriaLabel = (group) => {
  const label = (group.row_label || '').trim() || `row ${group.row_idx}`;
  const parts = [`${label}, ${group.sheet} row ${group.row_idx}`];
  parts.push(isUnmapped(group) ? 'unmapped' : `item ${itemText(group)}`);
  if (Array.isArray(group.reasons) && group.reasons.length) {
    parts.push(group.reasons.map(formatReviewReason).join(', '));
  }
  if (group.confidence) parts.push(`confidence ${formatConfidence(group.confidence)}`);
  parts.push(`status ${formatReviewerStatus(reviewerStatusOf(group))}`);
  return parts.join(', ');
};

export const ReviewGroupRow = ({
  group,
  runId,
  isSelected = false,
  onToggleSelect,
  isExpanded = false,
  onToggleExpand,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const unmapped = isUnmapped(group);

  const open = () => {
    if (unmapped) {
      navigate(fmdcPaths.unmapped(ROUTES.fmdc, runId, group.unmapped_row_id), {
        state: { backTo: `${location.pathname}${location.search}` },
      });
      return;
    }
    onToggleExpand(group.group_id);
  };

  const reasons = Array.isArray(group.reasons) ? group.reasons : [];
  const samples = sampleText(group);
  const reviewerStatus = reviewerStatusOf(group);

  return (
    <Row
      $columns={REVIEW_GRID_COLUMNS}
      $interactive
      $selected={isSelected}
      role="button"
      tabIndex={0}
      aria-expanded={unmapped ? undefined : isExpanded}
      aria-label={rowAriaLabel(group)}
      onClick={open}
      onKeyDown={rowKeyHandler(open)}
      data-testid={`review-group-${group.group_id}`}
    >
      <div>
        {!unmapped && onToggleSelect && (
          <SelectionCheckbox
            isChecked={isSelected}
            onChange={() => onToggleSelect(group.group_id)}
            ariaLabel={`Select ${(group.row_label || '').trim() || `row ${group.row_idx}`}`}
          />
        )}
      </div>
      <Locator title={`${group.sheet} · ${group.row_idx}`}>
        <Body7 truncate>{group.sheet}</Body7>
        <RowNumber>
          <Body7>· {group.row_idx}</Body7>
        </RowNumber>
      </Locator>
      <Truncate title={group.row_label || undefined}>
        <Body5 bold>{(group.row_label || '').trim() || EM_DASH}</Body5>
        {group.mixed_items && (
          <div>
            <MixedTag title={MIXED_NOTE}>
              <Body7>mixed</Body7>
            </MixedTag>
          </div>
        )}
      </Truncate>
      <Truncate title={itemText(group)}>
        <Body7>{itemText(group)}</Body7>
        {!unmapped && group.canonical_item_code && group.item_label && (
          <div>
            <Body7 isLowContrast>{group.canonical_item_code}</Body7>
          </div>
        )}
      </Truncate>
      <Truncate title={splitText(group)}>
        <Body7>{splitText(group)}</Body7>
      </Truncate>
      <Chips>
        {reasons.length === 0 ? (
          <Body7 isLowContrast>{EM_DASH}</Body7>
        ) : (
          reasons.map((reason) => {
            const tone = reviewReasonTone(reason);
            return (
              <Chip key={reason} $tone={tone === 'neutral' ? undefined : tone} title={reason}>
                {formatReviewReason(reason)}
              </Chip>
            );
          })
        )}
      </Chips>
      <div>
        <ConfidenceDot confidence={group.confidence} />
      </div>
      <div>
        {group.validation_status ? (
          <Chip $tone={VALIDATION_TONES[group.validation_status]}>
            {formatValidationStatus(group.validation_status)}
          </Chip>
        ) : (
          <Body7 isLowContrast>{EM_DASH}</Body7>
        )}
      </div>
      <div>
        <Body7>{formatCount(group.cells)}</Body7>
      </div>
      <Truncate title={samples}>
        <Body7>{samples}</Body7>
      </Truncate>
      <div>
        <StatusPill
          token={tokenFor(REVIEWER_STATUS_TOKENS, reviewerStatus)}
          label={formatReviewerStatus(reviewerStatus)}
        />
      </div>
      <Affordance aria-hidden="true">
        {unmapped ? (
          <Body7 isLowContrast>›</Body7>
        ) : (
          <Chevron $open={isExpanded}>
            <ChevronDown color="currentColor" />
          </Chevron>
        )}
      </Affordance>
    </Row>
  );
};
